import {
  CONTENT_PAGE_LABELS,
  type ContentBlock,
  type ContentPageStatus,
  type UpdateContentPageInput,
} from '@idpe/schema';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { Field } from '~/components/ui/Field.js';
import { Select } from '~/components/ui/Select.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { BlockEditor } from '~/features/contentPages/BlockEditor.js';
import {
  useContentPage,
  useContentPageVersions,
  useUpdateContentPage,
} from '~/features/contentPages/hooks.js';
import { ApiError } from '~/lib/api.js';

export const Route = createFileRoute('/_auth/pages/$slug')({
  component: PageEditPage,
});

function PageEditPage() {
  const { slug } = Route.useParams();
  const navigate = Route.useNavigate();
  const { data: page, isLoading, error } = useContentPage(slug);
  const versions = useContentPageVersions(slug);
  const update = useUpdateContentPage(slug);

  // Estado local de edição (não usa react-hook-form — manipulação direta de blocos)
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDesc, setSeoDesc] = useState('');
  const [blocos, setBlocos] = useState<ContentBlock[]>([]);
  const [status, setStatus] = useState<ContentPageStatus>('rascunho');
  const [mensagem, setMensagem] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (page) {
      setTitulo(page.titulo);
      setDescricao(page.descricao ?? '');
      setSeoTitle(page.seoTitle ?? '');
      setSeoDesc(page.seoDesc ?? '');
      setBlocos(page.blocos);
      setStatus(page.status);
      setDirty(false);
    }
  }, [page]);

  function markDirty() {
    setDirty(true);
  }

  async function handleSave(publish: boolean) {
    const payload: UpdateContentPageInput = {
      titulo,
      descricao: descricao || null,
      seoTitle: seoTitle || undefined,
      seoDesc: seoDesc || undefined,
      blocos,
      mensagem: mensagem || undefined,
      status: publish ? 'publicado' : status,
    };
    try {
      await update.mutateAsync(payload);
      toast.success(publish ? 'Página publicada.' : 'Alterações salvas.');
      setDirty(false);
      setMensagem('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao salvar.');
    }
  }

  if (isLoading) {
    return (
      <div className="page">
        <div className="dt__state">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="page">
        <header className="page-head">
          <p className="eyebrow">/ Erro</p>
          <h1 className="page-title">Página não encontrada.</h1>
          <p className="page-sub">{error?.displayMessage ?? 'Slug inválido.'}</p>
        </header>
        <Link to="/pages">
          <Button variant="secondary">Voltar à lista</Button>
        </Link>
      </div>
    );
  }

  const label = CONTENT_PAGE_LABELS[page.slug];

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ Conteúdo institucional</p>
        <h1 className="page-title">{label}</h1>
        <p className="page-sub" style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <Badge
            tone={status === 'publicado' ? 'success' : status === 'arquivado' ? 'muted' : 'neutral'}
          >
            {status}
          </Badge>
          {page.publicadoEm && (
            <span style={{ color: 'var(--fg-muted)' }}>
              publicada em {new Date(page.publicadoEm).toLocaleDateString('pt-BR')}
            </span>
          )}
          <span style={{ color: 'var(--fg-muted)' }}>·</span>
          <code style={{ fontSize: 'var(--fs-caption)' }}>{page.slug}</code>
        </p>
      </header>

      <section className="form-card">
        <h2 className="form-card__title">Identidade da página</h2>
        <div className="form-grid form-grid--two-col">
          <Field
            label="Título"
            required
            value={titulo}
            onChange={(e) => {
              setTitulo(e.target.value);
              markDirty();
            }}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ContentPageStatus);
              markDirty();
            }}
            options={[
              { value: 'rascunho', label: 'Rascunho' },
              { value: 'publicado', label: 'Publicado' },
              { value: 'arquivado', label: 'Arquivado' },
            ]}
          />
          <div className="form-grid__full">
            <Field
              label="Descrição (opcional)"
              hint="Texto curto para uso interno; não aparece no site."
              value={descricao}
              onChange={(e) => {
                setDescricao(e.target.value);
                markDirty();
              }}
            />
          </div>
        </div>
      </section>

      <section className="form-card">
        <h2 className="form-card__title">SEO</h2>
        <div className="form-grid form-grid--two-col">
          <Field
            label="Título SEO"
            hint={`${seoTitle.length} chars · ideal 50–60`}
            value={seoTitle}
            onChange={(e) => {
              setSeoTitle(e.target.value);
              markDirty();
            }}
            maxLength={70}
          />
          <Field
            label="Meta description"
            hint={`${seoDesc.length} chars · ideal 140–160`}
            value={seoDesc}
            onChange={(e) => {
              setSeoDesc(e.target.value);
              markDirty();
            }}
            maxLength={200}
          />
        </div>
      </section>

      <section className="form-card">
        <h2 className="form-card__title">Blocos editoriais</h2>
        <p className="form-card__sub">
          Ordene com as setas ↑/↓, oculte temporariamente sem perder o conteúdo, ou exclua de vez.
          Adicione blocos novos pelo botão no fim.
        </p>
        <BlockEditor
          blocos={blocos}
          onChange={(next) => {
            setBlocos(next);
            markDirty();
          }}
        />
      </section>

      <section className="form-card">
        <h2 className="form-card__title">Mensagem de versão (opcional)</h2>
        <p className="form-card__sub">
          Comentário curto que vai para o histórico de versões (estilo commit message).
        </p>
        <Field
          label="Mensagem"
          placeholder='Ex.: "Atualiza CTAs do hero", "Adiciona bloco de Cases"'
          value={mensagem}
          onChange={(e) => setMensagem(e.target.value)}
        />
      </section>

      <div className="form-actions form-actions--gap">
        <Link to="/pages">
          <Button variant="ghost">← Voltar</Button>
        </Link>
        <Button
          variant="secondary"
          onClick={() => void handleSave(false)}
          disabled={!dirty}
          loading={update.isPending}
        >
          Salvar alterações
        </Button>
        <Button variant="primary" onClick={() => void handleSave(true)} loading={update.isPending}>
          {status === 'publicado' ? 'Republicar' : 'Publicar agora'}
        </Button>
      </div>

      {versions.data && versions.data.length > 0 && (
        <section className="form-card">
          <h2 className="form-card__title">Histórico ({versions.data.length})</h2>
          <ol className="history-list">
            {versions.data.slice(0, 10).map((v) => (
              <li key={v.id} className="history-item">
                <div className="history-item__head">
                  <strong>{v.mensagem || '— sem mensagem —'}</strong>
                  <time>
                    {new Date(v.criadoEm).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </time>
                </div>
                <p className="history-item__author">por {v.autorNome}</p>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
